// External Libraries
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, of, Subscription, switchMap } from 'rxjs';
import { MessageService, PrimeTemplate } from 'primeng/api';

// Frontegg Services
import { ContextHolder, FronteggAppService, FronteggAuthService } from '@frontegg/angular';

// Project Services
import { AuthService } from '../../services/auth/auth.service';
import {
  ContentApiService,
  Area,
  DocumentType,
  KeyField as DocumentTypeKeyField
} from '../../services/content-api/content-api.service';
import { TenantApiService } from '../../services/tenant-api/tenant-api.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

// Project Models
import { Attachment } from '../../models/attachment';

// Utilities

import { NgxSpinnerService } from 'ngx-spinner';
import { AutoclassifierSdkService } from '../../services/autoclassifier-sdk/autoclassifier-sdk.service';
import { Document } from '../../models/document';
import { CaptureComponent } from '../../components/capture/capture.component';
import { WhatFixService } from '../../services/whatFix/whatfix.service';
import { BannerComponent } from '../../components/banner/banner.component';
import { Bind } from 'primeng/bind';
import { Toast } from 'primeng/toast';
import { TranslatePipe } from '@ngx-translate/core';
import { UrlVerificationComponent } from '@ai-document-processor/shared-libraries';

@Component({
  selector: 'app-add-in',
  templateUrl: './add-in.component.html',
  styleUrl: './add-in.component.scss',
  imports: [
    BannerComponent,
    CaptureComponent,
    Bind,
    Toast,
    PrimeTemplate,
    TranslatePipe,
    UrlVerificationComponent
  ]
})
export class AddInComponent implements OnInit, OnDestroy {
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private authService = inject(AuthService);
  private featureFlagService = inject(FeatureFlagService);
  private fronteggAuthService = inject(FronteggAuthService);
  private fronteggAppService = inject(FronteggAppService);
  private messageService = inject(MessageService);
  private spinner = inject(NgxSpinnerService);
  tenantService = inject(TenantApiService);
  private cdr = inject(ChangeDetectorRef);
  private whatFix = inject(WhatFixService);

  @ViewChild(CaptureComponent) captureComponent!: CaptureComponent;

  private subscription: Subscription = new Subscription();

  attachments$: BehaviorSubject<Attachment[]> = new BehaviorSubject<Attachment[]>([]);
  autoclassificationFlag$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isProcessingMessage = false;
  areas: Area[] = [];
  attachments: Attachment[] = [];
  documents: Document[] = [];
  currentAttachmentIndex: number = 0;
  currentDocumentType: DocumentType | undefined;
  currentMostConfidentDocument: any;
  documentForm!: FormGroup;
  featureFlagErrorText: string = '';
  filteredDocumentTypes: DocumentType[] = [];
  formStates: Map<string, any> = new Map();
  isAccessFlagLoading: boolean = true;
  isAutoClassificationEnabled: boolean = false;
  isAutoclassificationFlagLoading: boolean = true;
  isLoading = true;
  isUnsupportedFile: boolean = false;
  isUrlVerified: boolean = false;
  loadingSubscription: Subscription;
  outlookAddInAccessEnabled: boolean | null = null;
  showClassificationResults: boolean = false;
  tenantId: string = '';
  user?: any;
  showMergeDocuments: boolean = false;
  uploadedDocumentIds: string[] = [];
  isWebApps = false;
  isOutlook = true;

  constructor() {
    const fronteggAppService = this.fronteggAppService;

    this.loadingSubscription = fronteggAppService.isLoading$.subscribe(
      isLoading => (this.isLoading = isLoading)
    );
  }

  /**
   *
   */
  ngOnInit(): void {
    document.body.classList.add('overflow-scroll-issue');
    this.tenantId = localStorage.getItem('tenantId') || '';
    this.checkFeatureFlag();
  }

  /**
   * Run the feature flag check for the provided tenantId
   * @param tenantId the provided tenantId
   * TODO: centralize and parameterize
   */
  runFeatureFlagCheck(tenantId: string): void {
    this.isAccessFlagLoading = true;
    this.featureFlagService
      .initialize({ tenantId })
      .then(() =>
        this.subscription.add(
          this.featureFlagService
            .getFlag('outlook-add-in-access', false)
            .pipe(
              switchMap(flagValue => {
                this.isAccessFlagLoading = false;
                if (flagValue) {
                  this.outlookAddInAccessEnabled = true;
                  this.whatFix.initializeWithContext(this.tenantId);
                  this.startAddIn();
                } else {
                  this.isUrlVerified = false;
                  this.outlookAddInAccessEnabled = false;
                  this.featureFlagErrorText = 'addInFeatureFlagError';
                }
                return of(null);
              })
            )
            .subscribe()
        )
      )
      .catch(() => {
        this.isAccessFlagLoading = false;
        this.isUrlVerified = false;
        this.outlookAddInAccessEnabled = false;
      });
  }

  /**
   * Re-verifies the tenant URL and triggers feature flag check based on the tenant ID
   *  TODO: centralize and parameterize
   */
  onSuccessfulVerification(): void {
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      this.isAccessFlagLoading = true;
      this.runFeatureFlagCheck(tenantId);
    }
  }

  /**
   * Check the feature flag based on tenantId from localStorage.
   *  TODO: centralize and parameterize
   */
  checkFeatureFlag(): void {
    const tenantId = localStorage.getItem('tenantId');

    if (tenantId) {
      this.runFeatureFlagCheck(tenantId);
    } else {
      this.isAccessFlagLoading = false;
      this.isUrlVerified = false;
      this.outlookAddInAccessEnabled = null;
    }
  }

  /**
   * Starts the add-in by initializing the document form, loading data, and setting up communication with Outlook
   */
  startAddIn() {
    this.isUrlVerified = true;

    const organization = localStorage.getItem('vanityName');
    this.tenantId = localStorage.getItem('tenantId') || '';

    if (this.tenantId && organization) {
      this.subscription.add(
        this.tenantService.getTenantUrls(this.tenantId).subscribe({
          next: tenantUrls => {
            localStorage.setItem('contentURL', tenantUrls.contentUrl);
          },
          error: error => console.error('Error fetching tenant URLs:', error)
        })
      );
      this.subscription.add(
        this.fronteggAuthService.authState$.subscribe(authState => {
          if (!authState.isAuthenticated && !this.isLoading) {
            this.loginWithRedirect(this.tenantId, organization);
          } else {
            if (authState.user?.accessToken) {
              this.user = authState.user;
              this.authService.setAuthToken(this.user?.accessToken);

              this.initializeCommunication();
            }
          }
        })
      );
    } else {
      this.isUrlVerified = false;
    }
  }

  /**
   * Redirects the user to the login page with the specified tenant id and organization
   * @param tenantId - the id of the tenant to log in to
   * @param organization - the organization name associated with the tenant
   */
  loginWithRedirect(tenantId: string, organization: string): void {
    window.localStorage.setItem('FRONTEGG_AFTER_AUTH_REDIRECT_URL', window.location.href);
    this.fronteggAuthService.loginWithRedirect({
      tenantId: tenantId,
      organization: organization
    });
  }

  /**
   * Initializes communication between the add-in and Outlook
   */
  initializeCommunication(): void {
    // Signal to Outlook that dialog is ready to receive messages
    Office.context.ui.messageParent(JSON.stringify({ status: 'ready' }));

    // Add event handler to process messages received from Outlook
    Office.context.ui.addHandlerAsync(
      Office.EventType.DialogParentMessageReceived,
      this.processMessageFromParent.bind(this)
    );
  }

  /**
   * Processes a message received from the parent (Outlook)
   * @param arg - The message received from the parent
   */
  async processMessageFromParent(arg: any): Promise<void> {
    if (this.isProcessingMessage) return;
    this.isProcessingMessage = true;

    try {
      const data = JSON.parse(arg.message);
      const attachments = JSON.parse(data.message) as Attachment[];

      const uploadPromises = attachments.map(attachment => {
        const file = this.getBlobFileFromBase64(
          attachment.content.content,
          attachment.contentType,
          attachment.name
        );
        return this.autoclassifierSdkService.documentManagementSdk.uploadDocument(
          this.tenantId,
          file,
          undefined,
          undefined,
          this.authService.getAuthToken()
        );
      });

      const responses = await Promise.all(uploadPromises);
      this.uploadedDocumentIds = responses.map(response => response.id);
    } catch (error) {
      console.error('Error processing message from parent:', error);
      this.isProcessingMessage = false;
      this.cdr.detectChanges();
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.showErrorToast('Error processing message from parent:', errorMessage);
      return;
    } finally {
      this.isProcessingMessage = false;
      this.cdr.detectChanges();
      if (this.captureComponent) {
        this.captureComponent.startAdHocFiling(this.uploadedDocumentIds);
      }
    }
  }

  /**
   * Shows a message indicating an unsupported file format
   * @param isUnsupportedFile - The unsupported file status
   */
  showUnsupportedFileMessage(isUnsupportedFile: any): void {
    if (isUnsupportedFile.detail) {
      this.isUnsupportedFile = true;
    }
  }

  /**
   * Closes the dialog window and signals to Outlook that the dialog should be closed
   */
  closeDialog(): void {
    // Signal to Outlook to close the dialog window
    Office.context.ui.messageParent(JSON.stringify({ status: 'closeDialog' }));
    window.close();
  }

  /**
   * Shows an error toast message.
   * @param {string} toastMessage - The error message to display.
   * @param {string} [toastData] - Additional data to display in the toast.
   */
  showErrorToast(toastMessage: string, toastData?: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: toastMessage,
      key: 'errorToast',
      data: toastData ? toastData : '',
      life: 10000
    });
  }

  /**
   *
   */
  ngOnDestroy(): void {
    document.body.classList.remove('overflow-scroll-issue');
    this.loadingSubscription.unsubscribe();
    this.subscription.unsubscribe();
  }

  /**
   * Remove URL verificaetion items from local storage and reload the page to trigger the URL verification process
   */
  changeContentUrl(): void {
    localStorage.removeItem('tenantId');
    localStorage.removeItem('vanityName');
    location.reload();
  }

  /**
   * Signs out the user of the application and redirects them back to the log in page
   */
  signOut(): void {
    const baseUrl = ContextHolder.getContext().baseUrl;
    window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location}`;
  }

  /**
   * Convert base64 string to Blob
   * @param base64Content
   * @param contentType
   * @param name
   */
  private getBlobFileFromBase64(base64Content: string, contentType: string, name: string): File {
    // Remove data URL prefix if present
    const base64String = base64Content.includes(';base64,')
      ? base64Content.split(';base64,').pop() || ''
      : base64Content;

    try {
      // Decode base64 to binary string
      const binaryString = atob(base64String);

      // Convert binary string to byte array
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create Blob with PDF mime type
      const blob = new Blob([bytes], { type: contentType });
      return new File([blob], name, {
        type: contentType
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Error creating blob from base  64:', errorMessage);
      throw new Error(`Failed to create blob from base64: ${errorMessage}`);
    }
  }
}
